import {
  $applyNodeReplacement,
  $createTextNode,
  type EditorConfig,
  type LexicalNode,
  type NodeKey,
  type SerializedTextNode,
  type Spread,
  TextNode
} from 'lexical';

import styles from './formula-editor.scss';

interface SerializedMentionFields {
  invalid: boolean;
  mentionId: string;
  mentionName: string;
}

export type SerializedMentionNode = Spread<SerializedMentionFields, SerializedTextNode>;

/**
 * Represents a formula "@name" reference chip (equivalent to the draft-js `mention` entity).
 * Behaves as a single, atomic, non-editable text unit - it can be selected/deleted as a whole,
 * but its text content cannot be edited directly.
 */
export class MentionNode extends TextNode {
  public __invalid: boolean;
  public __mentionId: string;
  public __mentionName: string;

  public static getType(): string {
    return 'formula-mention';
  }

  public static clone(node: MentionNode): MentionNode {
    return new MentionNode(node.__mentionId, node.__mentionName, node.__invalid, node.__key);
  }

  public static importJSON(serializedNode: SerializedMentionNode): MentionNode {
    const node = $createMentionNode(serializedNode.mentionId, serializedNode.mentionName, serializedNode.invalid);
    node.setFormat(serializedNode.format);
    node.setDetail(serializedNode.detail);
    node.setMode(serializedNode.mode);
    node.setStyle(serializedNode.style);
    return node;
  }

  constructor(mentionId: string, mentionName: string, invalid = false, key?: NodeKey) {
    super(`@${mentionName}`, key);
    this.__mentionId = mentionId;
    this.__mentionName = mentionName;
    this.__invalid = invalid;
  }

  public createDOM(config: EditorConfig): HTMLElement {
    const dom = super.createDOM(config);
    dom.classList.add(styles.mention);
    dom.setAttribute('aria-invalid', String(this.__invalid));

    if (this.__invalid) {
      dom.classList.add(styles.invalidMention);
    }

    return dom;
  }

  public exportJSON(): SerializedMentionNode {
    return {
      ...super.exportJSON(),
      invalid: this.__invalid,
      mentionId: this.__mentionId,
      mentionName: this.__mentionName,
      type: 'formula-mention',
      version: 1
    };
  }

  public canInsertTextAfter(): boolean {
    return false;
  }

  public canInsertTextBefore(): boolean {
    return false;
  }

  public getMentionId(): string {
    return this.__mentionId;
  }

  public getMentionName(): string {
    return this.__mentionName;
  }

  public isInvalid(): boolean {
    return this.__invalid;
  }

  public isTextEntity(): true {
    return true;
  }
}

export function $createMentionNode(mentionId: string, mentionName: string, invalid = false): MentionNode {
  const node = new MentionNode(mentionId, mentionName, invalid);
  node.setMode('segmented');
  return $applyNodeReplacement(node);
}

export function $replaceTextWithMentionNode(
  nodeToReplace: TextNode,
  mentionId: string,
  mentionName: string
): MentionNode {
  const mentionNode = $createMentionNode(mentionId, mentionName);
  const trailingSpaceNode = $createTextNode(' ');

  nodeToReplace.replace(mentionNode);
  mentionNode.insertAfter(trailingSpaceNode);
  trailingSpaceNode.select(1, 1);

  return mentionNode;
}

export function $isMentionNode(node: LexicalNode | null | undefined): node is MentionNode {
  return node instanceof MentionNode;
}
